import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import './QuestionEditor.css';
import { toast } from 'react-toastify';

const QuestionEditor = ({ question, onSave, onCancel, inReview = false, onChange }) => {
    const [formData, setFormData] = useState(() => {
        // Start with 4 default empty options
        const defaultOptions = [
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
            { text: '', isCorrect: false }
        ];

        // Map incoming question options into the first slots
        const mergedOptions = defaultOptions.map((def, i) => {
            return question.Options && question.Options[i]
                ? { ...question.Options[i] }
                : def;
        });

        return {
            ...question,
            Options: mergedOptions,
            // Ensure these exist even if null in DB
            correctAnswerText: question.correctAnswerText || ''
        };
    });
    const [metadata, setMetadata] = useState({
        grades: [],
        subjects: [],
        topics: [],
        selectedGrade: '',
        selectedSubject: '',
        selectedTopic: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        onChange && onChange(formData);
    }, [formData, onChange]);

    const fetchData = async () => {
        try {
            const topicHierarchyRes = await api.get(`/admin/topic-hierarchy/${question.topicId}`);
            setMetadata(prev => ({
                ...prev,
                grades: topicHierarchyRes.data.data.grades,
                subjects: topicHierarchyRes.data.data.subjects,
                topics: topicHierarchyRes.data.data.topics,
                selectedGrade: topicHierarchyRes.data.selection.gradeId,
                selectedSubject: topicHierarchyRes.data.selection.subjectId,
                selectedTopic: topicHierarchyRes.data.selection.topicId
            }));
        } catch (err) { console.error("Error loading data"); }
    };

    const fetchSubjects = async (gradeId) => {
        try {
            const res = await api.get(`/admin/subjects?gradeId=${gradeId}`);
            setMetadata(prev => ({
                ...prev,
                subjects: res.data
            }));
        } catch (err) { console.error("Error loading subjects"); }
    };

    const fetchTopics = async (subjectId) => {
        try {
            const res = await api.get(`/admin/topics?subjectId=${subjectId}`);
            setMetadata(prev => ({
                ...prev,
                topics: res.data
            }));
        } catch (err) { console.error("Error loading topics"); }
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...formData.Options];
        newOptions[index].text = value;
        setFormData({ ...formData, Options: newOptions });
    };

    const handleCorrectOption = (index) => {
        const newOptions = formData.Options.map((opt, i) => ({
            ...opt,
            isCorrect: i === index
        }));
        setFormData({ ...formData, Options: newOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {

            // Logic to filter options based on type before sending
            let finalOptions = [];
            if (question.type === 'MCQ') finalOptions = formData.Options;
            if (question.type === 'TF') finalOptions = formData.Options.slice(0, 2);

            await api.put(`/admin/questions/${question.id}`, { ...formData, type: formData.type, options: finalOptions, reviewed: inReview });
            onSave();
            toast.success("Question updated!");
        } catch (err) {
            console.log(err);
            toast.error("Error updating question");
        }
    };

    const onGradeChange = (e) => {
        const gradeId = e.target.value;
        setFormData({ ...formData, gradeId, topicId: '' });
        fetchSubjects(gradeId);
    }

    const onSubjectChange = (e) => {
        const subjectId = e.target.value;
        setFormData({ ...formData, subjectId, topicId: '' });
        fetchTopics(subjectId);
    }

    const onTopicChange = (e) => {
        const topicId = e.target.value;
        setFormData({ ...formData, topicId });
    };

    return (
        <div className="question-container-editor">
            <form onSubmit={handleSubmit} className="question-form-editing">
                <div className="form-header-grid">
                    <div className="grade-subject-container">
                        {/* Grade Group */}
                        <div className="select-group">
                            <label>Grade</label>
                            <select value={metadata.selectedGrade} onChange={onGradeChange} required>
                                <option value="">Select Grade</option>
                                {metadata.grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                        </div>

                        {/* Subject Group */}
                        <div className="select-group">
                            <label>Subject</label>
                            <select value={metadata.selectedSubject} onChange={onSubjectChange} required>
                                <option value="">Select Subject</option>
                                {metadata.subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>

                        {/* Topic Group */}
                        <div className="select-group">
                            <label>Topic</label>
                            <select value={formData.topicId} onChange={onTopicChange} required>
                                <option value="">Select Topic</option>
                                {metadata.topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="topic-type-container">
                        <select value={formData.type} onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}>
                            <option value="MCQ">Multiple Choice</option>
                            <option value="TF">True / False</option>
                            <option value="SHORT">Short Answer</option>
                        </select>

                        <div className="points-input-wrapper">
                            <div className="input-with-icon">
                                <input
                                    type="number"
                                    placeholder="0"
                                    value={formData.points}
                                    onChange={e => setFormData({ ...formData, points: e.target.value })}
                                    min="0"
                                />
                                <span className="points-suffix">PTS</span>
                            </div>
                        </div>
                    </div>
                    <div className="image-url-container">
                        <div className="image-url-wrapper">
                            <div className="input-image-url">
                                <input
                                    type="text"
                                    placeholder="https://yourdomain.com/image.png"
                                    value={formData.imageUrl}
                                    onChange={e => setFormData({ ...formData, imageUrl: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="question-text-section">
                    <textarea
                        placeholder="Enter your question here..."
                        value={formData.text}
                        onChange={e => setFormData({ ...formData, text: e.target.value })}
                        required
                    />
                </div>

                <div className="answer-setup-section">
                    <h4>{formData.type === 'SHORT' ? 'Define Correct Answer' : 'Set Options & Correct Answer'}</h4>

                    {formData.type === 'SHORT' ? (
                        <div className="short-answer-wrapper">
                            <input
                                className="short-answer-input"
                                placeholder="e.g., Photosynthesis"
                                value={formData.correctAnswerText}
                                onChange={e => setFormData({ ...formData, correctAnswerText: e.target.value })}
                            />
                        </div>
                    ) : (
                        <div className="options-grid">
                            {(formData.type === 'MCQ' ? [0, 1, 2, 3] : [0, 1]).map(i => (
                                <div key={i} className={`option-card ${formData.Options[i].isCorrect ? 'is-correct' : ''}`}>
                                    <input
                                        type="radio"
                                        name="correct-ans"
                                        checked={formData.Options[i].isCorrect}
                                        onChange={() => handleCorrectOption(i)}
                                    />
                                    <input
                                        type="text"
                                        placeholder={formData.type === 'TF' ? (i === 0 ? "True" : "False") : `Option ${i + 1}`}
                                        value={formData.Options[i].text}
                                        onChange={e => handleOptionChange(i, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button type="submit" className="submit-btn">Save</button>
            </form>
        </div>
    );
};

export default QuestionEditor;